import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import {
    CodeBlock,
    BlockType,
    SettingType,
    Define,
    ValidationMessageType,
    ValidationMessage,
    SettingState,
    Setting,
    TibboDataType,
    AppBlockPort,
    SettingOption,
} from '@appblocks/codegeneration/types';
import BaseNode from '@appblocks/codegeneration/Nodes/BaseNode';
import NodePort from '@appblocks/codegeneration/Nodes/NodePort';
import NodeParameter from '@appblocks/codegeneration/Nodes/NodeParameter';
import * as tios from './tios';
import * as simulator from './simulator';


export default class ModbusWriteNode extends BaseNode {
    constructor() {
        super();
        this.name = 'Modbus Request';
        this.id = 'node_modbus_write';
        this.linkedFeature = 'modbus_master';
        this.isAsync = true;

        this.inputs = [
            {
                name: 'Write Register',
                portId: 'modbus_poll',
                params: [],
            },
        ];

        this.outputs = [
            {
                name: 'Response',
                portId: 'modbus_response',
                params: [
                    {
                        name: 'response_data',
                        dataType: TibboDataType.FLOAT,
                    },
                    {
                        name: 'device_id',
                        dataType: TibboDataType.WORD,
                    },
                    {
                        name: 'register_address',
                        dataType: TibboDataType.WORD,
                    },
                    {
                        name: 'reply',
                        dataType: TibboDataType.STRING,
                    },
                ],
            },
            {
                name: 'Timeout',
                portId: 'modbus_timeout',
                params: [],
            },
        ];

        const settings: Setting[] = [
            {
                id: 'DEVICE',
                name: 'Device',
                settingType: SettingType.OPTION,
                required: false,
                refId: 'modbus_device',
                default: '',
                options: [],
            },
            {
                id: 'SERIAL_PORT',
                name: 'Interface',
                settingType: SettingType.OPTION,
                required: true,
                default: '',
                options: [],
            },
            {
                id: 'FUNCTIONCODE',
                name: 'Function Code',
                settingType: SettingType.OPTION,
                required: true,
                default: 'ReadCoilStatus',
                options: [
                    {
                        name: 'Read COIL/Output (0x01)',
                        value: 'ReadCoilStatus',
                    },
                    {
                        name: 'Read Contact/Discrete Input (0x02)',
                        value: 'ReadInputStatus',
                    },
                    {
                        name: 'Read Holding Register (0x03)',
                        value: 'ReadHoldingRegisters',
                    },
                    {
                        name: 'Read Input Register (0x04)',
                        value: 'ReadInputRegisters',
                    },
                    {
                        name: 'Write Single Coil (0x05)',
                        value: 'WriteSingleCoil',
                    },
                    {
                        name: 'Write Single Register (0x06)',
                        value: 'PresetSingleRegister',
                    },
                ],
            },
            {
                id: 'DEVICEID',
                name: 'DeviceID',
                settingType: SettingType.STRINGVARIABLE,
                dataType: TibboDataType.BYTE,
                required: true,
                default: '',
            },
            {
                id: 'REGISTER_ADDRESS',
                name: 'Register Address',
                settingType: SettingType.STRINGVARIABLE,
                dataType: TibboDataType.WORD,
                required: true,
                default: '',
            },
            {
                id: 'LENGTH',
                name: 'Length (Number of registers)',
                settingType: SettingType.NUMBER,
                required: true,
                max: 125,
                default: '1',
            },
            {
                id: 'VARTYPE',
                name: 'Value Type',
                settingType: SettingType.OPTION,
                required: true,
                default: 'INTEGER',
                options: [
                    {
                        name: 'Unsigned Integer',
                        value: 'INTEGER',
                    },
                    {
                        name: 'Signed Integer',
                        value: 'SIGNED_INTEGER',
                    },
                    {
                        name: 'Floating Point Number',
                        value: 'FLOAT',
                    },
                ],
            },
            {
                id: 'ENDIAN',
                name: 'Endianness',
                settingType: SettingType.OPTION,
                required: true,
                default: 'BIG',
                options: [
                    {
                        name: 'Big Endian (ABCD)',
                        value: 'BIG',
                    },
                    {
                        name: 'Little Endian (CDAB)',
                        value: 'LITTLE',
                    },
                ],
            },
            {
                id: 'DATA',
                name: 'Data',
                settingType: SettingType.STRINGVARIABLE,
                dataType: TibboDataType.STRING,
                required: true,
                default: '',
            },
        ];

        this.settings = this.settings.concat(settings);
    }

    getSettingValue(settingId: string): string {
        if (settingId === 'DISPLAY_NAME') {
            const project: ProjectConfiguration = this.project;
            const DEVICE = this.getSettingValue('DEVICE');
            let DISPLAY_NAME = '';
            // const FUNCTIONCODE = this.getSettingValue('FUNCTIONCODE');
            const DATA = this.getSettingValue('DATA');
            const REGISTER_ADDRESS = this.getSettingValue('REGISTER_ADDRESS');
            // DISPLAY_NAME += ` ${FUNCTIONCODE}`;
            DISPLAY_NAME += `Data: ${DATA}`;
            DISPLAY_NAME += ` Address: ${REGISTER_ADDRESS}`;


            return DISPLAY_NAME;
        }
        return super.getSettingValue(settingId);
    }

    validate(): ValidationMessage[] {
        // if both device and interface are undefined, return error
        const DEVICE = this.getSettingValue('DEVICE');
        const INTERFACE = this.getSettingValue('SERIAL_PORT');
        if (DEVICE === '' && INTERFACE === '') {
            return [{
                messageType: ValidationMessageType.ERROR,
                message: 'Device or interface must be defined',
                featureId: this.getID(),
                settingId: 'SERIAL_PORT',
            }];
        }
        return super.validate();
    }

    getSettingOptions(settingId: string): SettingOption[] | undefined {
        const project: ProjectConfiguration = this.project;
        switch (settingId) {
            case 'SERIAL_PORT':
                return project.getModbusSerialPorts();
            default:
                return super.getSettingOptions(settingId);
        }
    }

    getState(settingId: string): SettingState {
        const project: ProjectConfiguration = this.project;
        const DEVICE = this.getSettingValue('DEVICE');
        const FUNCTIONCODE = this.getSettingValue('FUNCTIONCODE');
        let PROTOCOL = 'modbus_RTU';
        if (DEVICE !== '') {
            const device = project.getFeature(DEVICE);
            PROTOCOL = device?.getSettingValue('PROTOCOL');
        }
        switch (settingId) {
            case 'SERIAL_PORT':
                if (DEVICE !== '') {
                    return SettingState.HIDDEN;
                }
                return SettingState.ENABLED;
            case 'DEVICEID':
                if (DEVICE !== '' && PROTOCOL === 'modbus_RTU') {
                    return SettingState.HIDDEN;
                }
                return SettingState.ENABLED;
            case 'DATA':
                if (FUNCTIONCODE === 'WriteSingleCoil' || FUNCTIONCODE === 'PresetSingleRegister') {
                    return SettingState.ENABLED;
                }
                return SettingState.HIDDEN;
            case 'LENGTH':
            case 'VARTYPE':
            case 'ENDIAN':
                if (FUNCTIONCODE === 'WriteSingleCoil' || FUNCTIONCODE === 'PresetSingleRegister') {
                    return SettingState.HIDDEN;
                }
                return SettingState.ENABLED;
            default:
                return super.getState(settingId);
        }
    }

    getCodeBlocks(): CodeBlock[] {
        if (this.project.runtime === 'simulator') {
            return simulator.getCodeBlocks(this);
        }
        return tios.getCodeBlocks(this);
    }
}
